import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { IntegrationHeader } from 'components/IntegrationHeader';
import { useStore } from 'models/Provider';

import { SAPEmployeeType } from './api';
import { useCreateSapApplication, useSapApplication } from './hooks';

export const ReykjavikSAP = (props: { applicationId: string }) => {
  const { applicationId } = props;
  const sapApplicationQuery = useSapApplication(applicationId);
  const data = sapApplicationQuery.data;
  if (sapApplicationQuery.isLoading || !data) {
    return <Spinner />;
  }
  return (
    <Stack space={6}>
      <IntegrationHeader
        integration={{
          name: data.name,
          title: data.title,
          description: data.description,
        }}
      />
      {data.exists ? (
        <ExistsCard {...data} />
      ) : (
        <NotExistsCard applicationId={applicationId} contractSigned={data.contractSigned} />
      )}
    </Stack>
  );
};

const ExistsCard = ({ sapId, createdBy, dateCreated }: SAPEmployeeType) => {
  const { t } = useScopedTranslate(locale);
  const { lang } = useStore();
  return (
    <Card space={6}>
      <Stack space={4}>
        <Stack space={1} orientation="horizontal" align="center">
          <IconFormIndicatorSuccess size={16} />
          <Text font="regular14" color="green100" text={t('createdInSap')} />
        </Stack>
        <div>
          <Text font="semibold14" text="SAP" />
          <Text font="regular14" text={t('createdBy', { userName: createdBy ?? '' })} />
          {dateCreated && (
            <Text font="regular14" text={abbreviateDate(dateCreated, lang, 'datetime')} />
          )}
          <Text font="regular14" text={t('sapId', { sapid: sapId ?? '' })} />
        </div>
      </Stack>
    </Card>
  );
};

const NotExistsCard = ({
  applicationId,
  contractSigned,
}: {
  applicationId: string;
  contractSigned: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateSapApplication(applicationId);
  return (
    <Card space={6}>
      <Stack space={4}>
        <Stack space={1} orientation="horizontal" align="center">
          <IconFormIndicatorInfo color="indigo100" size={16} />
          <Text font="regular14" color="indigo100" text={t('notCreated')} />
        </Stack>
        <Stack orientation="horizontal" justify="flex-end">
          <Button
            iconLeft={<IconAddPlus />}
            variant="secondary"
            text={t('addToSap')}
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isLoading || !contractSigned}
            tooltip={!contractSigned ? t('cannotCreate') : ''}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    notCreated: 'Not created',
    addToSap: 'Add employee to SAP',
    createdInSap: 'Employee created',
    createdBy: 'Created by: {userName}',
    sapId: 'SAP ID: {sapid}',
    cannotCreate:
      'In order to create a new employee in SAP both parties need to have signed the contract in the step here above',
  },
  is: {
    notCreated: 'Ekki stofnaður',
    addToSap: 'Stofna starfsmann í SAP',
    createdInSap: 'Starfsmaður stofnaður',
    sapId: 'Starfsmannanúmer: {sapid}',
    cannotCreate:
      'Til þess að bæta við nýjum starfsmanni í SAP þurfa báðir aðilar að hafa undirritað ráðningarsamning í skrefinu hér á undan',
  },
};
